/**
 * TransferHistoryTab — history tab showing verified transfer records.
 * Extracted from TransferVerification.tsx for readability.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { VerifiedTransfer } from '../../../services/inventoryService';
import { formatVerifiedTime } from './useTransferData';
import {
  CheckCircle,
  AlertTriangle,
  Check,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  History,
  ChevronDown,
  ChevronUp,
  Pause,
} from 'lucide-react';

interface TransferHistoryTabProps {
  historyTransfers: VerifiedTransfer[];
  loadingHistory: boolean;
  historyPage: number;
  setHistoryPage: React.Dispatch<React.SetStateAction<number>>;
  historyTotalPages: number;
  expandedHistoryTransfer: string | null;
  setExpandedHistoryTransfer: React.Dispatch<React.SetStateAction<string | null>>;
  expandedHistoryBoxes: Set<string>;
  setExpandedHistoryBoxes: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const TransferHistoryTab: React.FC<TransferHistoryTabProps> = ({
  historyTransfers,
  loadingHistory,
  historyPage,
  setHistoryPage,
  historyTotalPages,
  expandedHistoryTransfer,
  setExpandedHistoryTransfer,
  expandedHistoryBoxes,
  setExpandedHistoryBoxes,
}) => {
  const { t } = useLanguage();

  return (
    <div className="p-4">
      {loadingHistory ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : historyTransfers.length === 0 ? (
        <div className="text-center py-12">
          <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">{t('transferVerification.noHistory')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {historyTransfers.map((transfer) => (
            <div
              key={transfer.transfer_number}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Transfer header - clickable */}
              <button
                onClick={() => setExpandedHistoryTransfer(
                  expandedHistoryTransfer === transfer.transfer_number ? null : transfer.transfer_number
                )}
                className="w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1 mr-2">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {t('transferVerification.transfer')} #{transfer.transfer_number}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatVerifiedTime(transfer.verified_at)}
                    </p>
                    {transfer.verified_by_name && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {transfer.verified_by_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Status badges */}
                    <div className="flex flex-wrap gap-1">
                      {transfer.approved_count > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full whitespace-nowrap">
                          {transfer.approved_count} {t('transferVerification.approved')}
                        </span>
                      )}
                      {transfer.on_hold_count > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full whitespace-nowrap">
                          {transfer.on_hold_count} {t('transferVerification.onHold')}
                        </span>
                      )}
                      {transfer.rejected_count > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full whitespace-nowrap">
                          {transfer.rejected_count} {t('transferVerification.rejected')}
                        </span>
                      )}
                    </div>
                    {expandedHistoryTransfer === transfer.transfer_number ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded item details */}
              {expandedHistoryTransfer === transfer.transfer_number && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                  <div className="space-y-3">
                    {/* Group items by item_number to consolidate accepted/on_hold/rejected into one card */}
                    {(() => {
                      const groupedItems = transfer.items.reduce((acc, item) => {
                        const key = item.item_number;
                        if (!acc[key]) {
                          acc[key] = {
                            item_number: item.item_number,
                            item_name: item.item_name,
                            expected_quantity: 0,
                            expected_weight: 0,
                            acceptedQty: 0,
                            acceptedWeight: 0,
                            onHoldQty: 0,
                            onHoldWeight: 0,
                            rejectedQty: 0,
                            rejectedWeight: 0,
                            notInTransfer: false,
                          };
                        }

                        const qty = Math.round(parseFloat(String(item.actual_quantity || 0)));
                        const weight = parseFloat(String(item.actual_weight || 0));
                        const expQty = Math.round(parseFloat(String(item.expected_quantity || 0)));
                        const expWeight = parseFloat(String(item.expected_weight || 0));

                        // Check if this is a "not in transfer" item (expected = 0 or notes contain "Not in transfer")
                        const isNotInTransfer = expQty === 0 || (item.verification_notes && item.verification_notes.includes('Not in transfer'));
                        if (isNotInTransfer) {
                          acc[key].notInTransfer = true;
                        }

                        // Only count expected once per item (take max to handle duplicates)
                        acc[key].expected_quantity = Math.max(acc[key].expected_quantity, expQty);
                        acc[key].expected_weight = Math.max(acc[key].expected_weight, expWeight);

                        // Aggregate by verification status
                        // Backend stores: 'verified' (approved), 'on_hold', 'rejected'
                        const status = item.verification_status as string;
                        if (status === 'approved' || status === 'verified') {
                          acc[key].acceptedQty += qty;
                          acc[key].acceptedWeight += weight;
                        } else if (status === 'hold' || status === 'on_hold') {
                          acc[key].onHoldQty += qty;
                          acc[key].onHoldWeight += weight;
                        } else if (status === 'rejected') {
                          acc[key].rejectedQty += qty;
                          acc[key].rejectedWeight += weight;
                        }

                        return acc;
                      }, {} as Record<string, {
                        item_number: string;
                        item_name?: string;
                        expected_quantity: number;
                        expected_weight: number;
                        acceptedQty: number;
                        acceptedWeight: number;
                        onHoldQty: number;
                        onHoldWeight: number;
                        rejectedQty: number;
                        rejectedWeight: number;
                        notInTransfer: boolean;
                      }>);

                      // Separate items in transfer from items not in transfer
                      const inTransferItems = Object.values(groupedItems).filter(item => !item.notInTransfer);
                      const notInTransferItems = Object.values(groupedItems).filter(item => item.notInTransfer);

                      return (
                        <>
                          {/* Items in transfer */}
                          {inTransferItems.map((groupedItem) => {
                            const hasRejected = groupedItem.rejectedQty > 0;
                            const hasOnHold = groupedItem.onHoldQty > 0;
                            const hasAccepted = groupedItem.acceptedQty > 0;

                            return (
                              <div
                                key={groupedItem.item_number}
                                className={`p-4 rounded-lg border ${hasRejected && !hasAccepted && !hasOnHold
                                    ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                                    : hasRejected || hasOnHold
                                      ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
                                      : 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                                  }`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <p className="font-semibold text-gray-900 dark:text-white">
                                      {groupedItem.item_name || groupedItem.item_number}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                      {groupedItem.item_number}
                                    </p>
                                  </div>
                                  {hasRejected && !hasAccepted && !hasOnHold ? (
                                    <X className="w-6 h-6 text-red-600 dark:text-red-400" />
                                  ) : hasRejected || hasOnHold ? (
                                    <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                                  ) : (
                                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                                  )}
                                </div>

                                {/* 2x2 grid on mobile, 4-column on larger screens */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                                  <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                                    <span className="text-gray-500 dark:text-gray-400 text-xs block">{t('transferVerification.expected')}:</span>
                                    <p className="font-mono text-gray-900 dark:text-white text-base">
                                      {groupedItem.expected_quantity} / {groupedItem.expected_weight.toFixed(2)} lbs
                                    </p>
                                  </div>
                                  <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                                    <span className="text-green-600 dark:text-green-400 text-xs block">{t('transferVerification.accepted')}:</span>
                                    <p className="font-mono text-green-600 dark:text-green-400 text-base">
                                      {groupedItem.acceptedQty} / {groupedItem.acceptedWeight.toFixed(2)} lbs
                                    </p>
                                  </div>
                                  <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                                    <span className="text-amber-600 dark:text-amber-400 text-xs block">{t('transferVerification.onHold')}:</span>
                                    <p className="font-mono text-amber-600 dark:text-amber-400 text-base">
                                      {groupedItem.onHoldQty} / {groupedItem.onHoldWeight.toFixed(2)} lbs
                                    </p>
                                  </div>
                                  <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                                    <span className="text-red-600 dark:text-red-400 text-xs block">{t('transferVerification.rejected')}:</span>
                                    <p className="font-mono text-red-600 dark:text-red-400 text-base">
                                      {groupedItem.rejectedQty} / {groupedItem.rejectedWeight.toFixed(2)} lbs
                                    </p>
                                  </div>
                                </div>

                                {/* Per-box breakdown — collapsible, from scan_details */}
                                {(() => {
                                  // Collect scan_details from all lots of this item (in-transfer only)
                                  const itemLots = transfer.items.filter(i => i.item_number === groupedItem.item_number && !(Math.round(parseFloat(String(i.expected_quantity || 0))) === 0 || (i.verification_notes && i.verification_notes.includes('Not in transfer'))));
                                  const allScanDetails = itemLots.flatMap(lot => lot.scan_details || []).sort((a, b) => a.scan_order - b.scan_order);
                                  if (allScanDetails.length === 0) return null;
                                  const boxKey = `${transfer.transfer_number}-${groupedItem.item_number}`;
                                  const isExpanded = expandedHistoryBoxes.has(boxKey);
                                  return (
                                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                      <button
                                        onClick={() => setExpandedHistoryBoxes(prev => {
                                          const next = new Set(prev);
                                          if (next.has(boxKey)) next.delete(boxKey); else next.add(boxKey);
                                          return next;
                                        })}
                                        className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-1"
                                      >
                                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                        {t('transferVerification.boxDetails')} ({allScanDetails.length})
                                      </button>
                                      {isExpanded && (
                                        <div className="space-y-1">
                                          {allScanDetails.map((detail) => {
                                            const isApproved = detail.status === 'accepted';
                                            const isOnHold = detail.status === 'on_hold';
                                            return (
                                              <div key={detail.scan_order} className="flex items-center justify-between text-xs">
                                                <div className="flex items-center gap-1.5">
                                                  {isApproved ? (
                                                    <Check className="w-3 h-3 flex-shrink-0 text-green-600 dark:text-green-400" />
                                                  ) : isOnHold ? (
                                                    <Pause className="w-3 h-3 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                                                  ) : (
                                                    <X className="w-3 h-3 flex-shrink-0 text-red-600 dark:text-red-400" />
                                                  )}
                                                  <span className="text-gray-500 dark:text-gray-400 font-mono">#{detail.scan_order}</span>
                                                  <span className={
                                                    isApproved ? 'text-green-600 dark:text-green-400'
                                                      : isOnHold ? 'text-amber-600 dark:text-amber-400'
                                                        : 'text-red-600 dark:text-red-400'
                                                  }>
                                                    {isApproved ? t('transferVerification.approved') : isOnHold ? t('transferVerification.onHold') : t('transferVerification.rejected')}
                                                    {detail.remark ? ` - ${detail.remark}` : ''}
                                                  </span>
                                                </div>
                                                <span className="text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">{parseFloat(String(detail.weight)).toFixed(2)} lbs</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })}

                          {/* Items not in transfer */}
                          {notInTransferItems.length > 0 && (
                            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                <h3 className="font-semibold text-orange-800 dark:text-orange-200">
                                  {t('transferVerification.itemsNotInTransfer')}
                                </h3>
                              </div>
                              <div className="space-y-2 text-sm">
                                {notInTransferItems.map((item) => {
                                  const itemLots = transfer.items.filter(i => i.item_number === item.item_number && (Math.round(parseFloat(String(i.expected_quantity || 0))) === 0 || (i.verification_notes && i.verification_notes.includes('Not in transfer'))));
                                  return (
                                    <div key={item.item_number}>
                                      <div className="flex items-start gap-1">
                                        <X className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div>
                                          <p className="text-gray-700 dark:text-gray-300 font-medium">
                                            {item.item_name || item.item_number}
                                          </p>
                                          <p className="text-gray-500 dark:text-gray-400 text-xs">
                                            {item.item_number}
                                          </p>
                                        </div>
                                      </div>
                                      {/* Per-box scan details for not-in-transfer items — collapsible */}
                                      {(() => {
                                        const allScanDetails = itemLots.flatMap(lot => lot.scan_details || []).sort((a, b) => a.scan_order - b.scan_order);
                                        if (allScanDetails.length === 0) return null;
                                        const boxKey = `${transfer.transfer_number}-nit-${item.item_number}`;
                                        const isExpanded = expandedHistoryBoxes.has(boxKey);
                                        return (
                                          <div className="ml-5 mt-1">
                                            <button
                                              onClick={() => setExpandedHistoryBoxes(prev => {
                                                const next = new Set(prev);
                                                if (next.has(boxKey)) next.delete(boxKey); else next.add(boxKey);
                                                return next;
                                              })}
                                              className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-0.5"
                                            >
                                              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                              {t('transferVerification.boxDetails')} ({allScanDetails.length})
                                            </button>
                                            {isExpanded && (
                                              <div className="space-y-0.5">
                                                {allScanDetails.map((detail) => (
                                                  <div key={detail.scan_order} className="flex items-center justify-between text-xs">
                                                    <div className="flex items-center gap-1.5">
                                                      <X className="w-3 h-3 flex-shrink-0 text-red-600 dark:text-red-400" />
                                                      <span className="text-gray-500 dark:text-gray-400 font-mono">#{detail.scan_order}</span>
                                                      <span className="text-red-600 dark:text-red-400">
                                                        {t('transferVerification.rejected')}{detail.remark ? ` - ${detail.remark}` : ''}
                                                      </span>
                                                    </div>
                                                    <span className="text-gray-500 dark:text-gray-400 ml-2">{parseFloat(String(detail.weight)).toFixed(2)} lbs</span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Pagination */}
          {historyTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                disabled={historyPage === 1}
                className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {historyPage} / {historyTotalPages}
              </span>
              <button
                onClick={() => setHistoryPage(p => Math.min(historyTotalPages, p + 1))}
                disabled={historyPage === historyTotalPages}
                className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransferHistoryTab;
